import { useEffect, useState } from 'react';
import { agencyAPI, userAPI } from '@/lib/api';

// Shared cache across hook instances so opening multiple drawers or
// reopening the same drawer doesn't re-fetch the directory unnecessarily.
let cachedAgencies = null;
let cachedAgenciesPromise = null;
const userCache = new Map(); // user_id → { name, email, agency_id }
let userDirectoryFetched = false;
let userDirectoryPromise = null;

async function loadAgencies() {
  if (cachedAgencies) return cachedAgencies;
  if (cachedAgenciesPromise) return cachedAgenciesPromise;
  cachedAgenciesPromise = agencyAPI.list()
    .then((r) => {
      const map = {};
      (r.data || []).forEach((a) => {
        map[a.agency_id] = a.agency_name || a.name || a.agency_id;
      });
      cachedAgencies = map;
      return map;
    })
    .catch(() => {
      cachedAgencies = {};
      return {};
    })
    .finally(() => { cachedAgenciesPromise = null; });
  return cachedAgenciesPromise;
}

function primeCacheWithUser(user) {
  if (!user?.user_id || userCache.has(user.user_id)) return;
  const primaryRole = (user.roles || []).find((r) => r.status === 'active') || (user.roles || [])[0];
  userCache.set(user.user_id, {
    name: user.name || user.email || user.user_id,
    email: user.email || '',
    agency_id: primaryRole?.agency_id || null,
    role: primaryRole?.role || null,
  });
}

async function loadUserDirectory(canFetchAll) {
  // Only KR-role callers can hit /api/users. For non-KR viewers we skip
  // the fetch entirely — the currentUser record (primed above) will
  // resolve own-row labels, and any other user_id renders as
  // "Former user" which is the correct semantic for "no directory
  // access available to me".
  if (!canFetchAll) return;
  if (userDirectoryFetched) return;
  if (userDirectoryPromise) return userDirectoryPromise;
  userDirectoryPromise = (async () => {
    try {
      const r = await userAPI.list();
      (r.data || []).forEach((u) => {
        const primaryRole = (u.roles || []).find((role) => role.status === 'active') || (u.roles || [])[0];
        userCache.set(u.user_id, {
          name: u.name || u.email || u.user_id,
          email: u.email || '',
          agency_id: primaryRole?.agency_id || null,
          role: primaryRole?.role || null,
        });
      });
      userDirectoryFetched = true;
    } catch {
      // leave cache as-is; callers fall back to labels.
    } finally {
      userDirectoryPromise = null;
    }
  })();
  return userDirectoryPromise;
}

/**
 * Loads name/agency lookup maps.
 *
 * Non-KR viewers cannot enumerate the user directory, so we prime the
 * cache with the currentUser record only. KR viewers get the full
 * directory fetched once and cached across the session.
 *
 * @param {object} params
 * @param {object} params.currentUser  From AuthContext — required.
 * @param {boolean} params.canFetchAll True when the viewer can list the full user directory.
 * @param {boolean} params.enabled     Skip work when the panel isn't rendered.
 */
export function useEntityMaps({ currentUser, canFetchAll = false, enabled = true } = {}) {
  const [users, setUsers] = useState(() => {
    if (currentUser) primeCacheWithUser(currentUser);
    return Object.fromEntries(userCache);
  });
  const [agencies, setAgencies] = useState(cachedAgencies || {});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (currentUser) primeCacheWithUser(currentUser);
    let alive = true;
    (async () => {
      await Promise.all([
        loadAgencies().then((m) => alive && setAgencies(m)),
        loadUserDirectory(canFetchAll).then(() => alive && setUsers(Object.fromEntries(userCache))),
      ]);
      if (alive) {
        setUsers(Object.fromEntries(userCache));
        setReady(true);
      }
    })();
    return () => { alive = false; };
  }, [enabled, canFetchAll, currentUser]);

  return { users, agencies, ready };
}

export function resolveUserLabel(users, userId, currentUser) {
  if (!userId) return 'System';
  if (currentUser && userId === currentUser.user_id) {
    return currentUser.name || currentUser.email || userId;
  }
  const u = users[userId];
  if (!u) return 'Former user';
  return u.name || u.email || userId;
}

export function resolveAgencyLabel(agencies, users, userId, currentUser) {
  if (currentUser && userId === currentUser.user_id) {
    const role = (currentUser.roles || []).find((r) => r.status === 'active') || (currentUser.roles || [])[0];
    if (role?.agency_id) return agencies[role.agency_id] || role.agency_id;
  }
  const u = users[userId];
  if (!u || !u.agency_id) return '—';
  return agencies[u.agency_id] || u.agency_id;
}
